import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Tag, UserPlus, FileText, Wallet, Users,
  ScrollText, Settings2, Settings as SettingsIcon, ChevronDown, LogOut, Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Wordmark } from "@/components/Wordmark";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SUPER_ADMIN, submissions, applications, signOutAdmin } from "@/lib/mock-data";
import { PageGate } from "@/components/PageGate";
import { ThemeToggle } from "@/components/ThemeToggle";
import { HelpCircle } from "lucide-react";
import { canVisitPage, useCurrentAdminRole } from "@/lib/permissions";
import { resetDemoState, demoOverrideCount } from "@/lib/demoState";
import { useStoreVersion } from "@/hooks/useStore";
import { RotateCcw } from "lucide-react";

const FLAGGED_STATUSES = new Set([
  "Flagged", "FlaggedFormat", "FlaggedDuplicate", "AwaitingMemberResponse",
]);

const sections = [
  { label: "Overview",   items: [
    { to: "/admin/dashboard",    label: "Dashboard",     icon: LayoutDashboard, page: "dashboard" },
  ]},
  { label: "Operations", items: [
    { to: "/admin/deals",        label: "Deals",         icon: Tag, page: "deals" },
    { to: "/admin/applications", label: "Applications",  icon: UserPlus, badge: null, badgeTone: "pending", page: "applications" },
    { to: "/admin/submissions",  label: "Submissions",   icon: FileText, badge: null, badgeTone: "flagged", page: "submissions" },
    { to: "/admin/payments",     label: "Payments",      icon: Wallet, page: "payments" },
  ]},
  { label: "Directory",  items: [
    { to: "/admin/members",      label: "Members",       icon: Users, page: "members" },
  ]},
  { label: "System",     items: [
    { to: "/admin/audit",        label: "Audit Log",     icon: ScrollText, page: "audit" },
    { to: "/admin/brands",       label: "Brands & Rules",icon: Settings2, page: "brands" },
    { to: "/admin/settings",     label: "Settings",      icon: SettingsIcon, page: "settings" },
  ]},
];

export function AdminShell({ children, pageTitle, breadcrumb, headerActions }) {
  const navigate = useNavigate();
  useStoreVersion(); // re-render this subtree on any global store mutation
  const role = useCurrentAdminRole();
  const flaggedCount = submissions.filter((s) => FLAGGED_STATUSES.has(s.status)).length;
  const pendingAppsCount = applications.filter((a) => a.status === "PendingReview").length;
  // Filter sidebar to pages the current role can visit, and drop sections that
  // end up with zero allowed items.
  const sectionsWithBadges = sections
    .map((sec) => ({
      ...sec,
      items: sec.items
        .filter((it) => canVisitPage(it.page, role))
        .map((it) => {
          if (it.to === "/admin/submissions") return { ...it, badge: flaggedCount > 0 ? flaggedCount : null };
          if (it.to === "/admin/applications") return { ...it, badge: pendingAppsCount > 0 ? pendingAppsCount : null };
          return it;
        }),
    }))
    .filter((sec) => sec.items.length > 0);
  const initials = `${SUPER_ADMIN.firstName?.[0] || "T"}${SUPER_ADMIN.lastName?.[0] || "M"}`;

  return (
    <div className="min-h-screen bg-surface-alt" style={{ minWidth: 1280 }}>
      <aside className="fixed left-0 top-0 h-screen w-60 bg-surface border-r border-line flex flex-col z-30">
        <div className="px-5 py-5 border-b border-line">
          <Link to="/admin/dashboard" className="flex items-center">
            <Wordmark size="sm" badge="Admin" />
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
          {sectionsWithBadges.map((sec) => (
            <div key={sec.label}>
              <div className="px-3 mb-1.5 text-eyebrow uppercase text-ink-hint">{sec.label}</div>
              <div className="space-y-1">
                {sec.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => cn("sidenav-row", isActive && "active")}
                  >
                    <item.icon className="size-4" strokeWidth={2} />
                    <span className="flex-1">{item.label}</span>
                    {item.badge != null && (
                      <span className={cn(
                        "ml-auto inline-flex items-center justify-center rounded-full text-white text-[10px] font-semibold size-5 leading-none",
                        item.badgeTone === "pending" ? "bg-brand-700" : "bg-orange-500",
                      )}>
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-line">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-surface-alt">
                <Avatar className="size-8">
                  <AvatarFallback className="bg-ink text-white text-caption font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-body-sm font-semibold text-ink truncate">
                    {SUPER_ADMIN.firstName} {SUPER_ADMIN.lastName}
                  </div>
                  <div className="text-caption text-ink-subtle truncate">{SUPER_ADMIN.role}</div>
                </div>
                <ChevronDown className="size-4 text-ink-hint" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
              <DropdownMenuItem disabled className="text-caption">
                {SUPER_ADMIN.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
                <SettingsIcon className="size-4 mr-2" /> Admin settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={resetDemoState} className="text-amber-700 focus:text-amber-800 focus:bg-amber-50" data-testid="reset-demo-btn">
                <RotateCcw className="size-4 mr-2" /> Reset demo state{demoOverrideCount() > 0 && (
                  <span className="ml-auto text-caption tabular-nums">({demoOverrideCount()})</span>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { signOutAdmin(); navigate("/admin/login"); }} data-testid="admin-sign-out-btn">
                <LogOut className="size-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="ml-60">
        <div className="sticky top-0 z-20 bg-surface border-b border-line">
          <div className="px-8 h-14 flex items-center gap-6">
            <div className="min-w-0 flex-1">
              {breadcrumb && (
                <div className="text-caption text-ink-subtle">{breadcrumb}</div>
              )}
              {pageTitle && (
                <h1 className="text-h2 text-ink font-brand truncate">{pageTitle}</h1>
              )}
            </div>
            <div className="flex items-center gap-2 w-72">
              <div className="relative w-full">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint pointer-events-none" />
                <Input placeholder="Search members, deals, runs…" className="pl-8 h-9 text-body-sm bg-surface-alt border-line" />
              </div>
            </div>
            <div className="flex items-center gap-2">{headerActions}<ThemeToggle /></div>
          </div>
        </div>

        <main className="px-8 py-6">
          <PageGate>{children}</PageGate>
        </main>
      </div>

      {/* Floating help button — opens the Cmd+K demo state inspector */}
      <button
        type="button"
        onClick={() => window.dispatchEvent(new Event("open-demo-inspector"))}
        data-testid="floating-help-btn"
        title="Open demo state inspector (⌘K)"
        className="fixed bottom-20 right-5 z-40 size-11 rounded-full bg-ink hover:bg-slate-800 text-white grid place-items-center shadow-lg transition-colors"
      >
        <HelpCircle className="size-5" strokeWidth={2} />
      </button>
    </div>
  );
}

export default AdminShell;
